import { useCallback, useEffect, useRef, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/cn'
import { Modal } from '@/components/Modal'
import { Button } from '@/components/ui'

/**
 * Bekräftelseknappen man håller in inför butikspersonalen.
 *
 * Att hålla in i 3 sekunder i stället för att trycka är ingen dekoration:
 * inlösen är permanent, och ett oavsiktligt tryck i fickan eller en darrig
 * tumme i kassan skulle bränna en rabatt. Håll-tiden ger också personalen
 * tid att titta på skärmen innan stämpeln slår ned.
 *
 * Själva fyllnaden animeras av CSS (se .hold-fill i index.css). Här sköts
 * bara tillståndet, haptiken och nedräkningstexten – tre omritningar i
 * stället för hundraåttio.
 */
const HOLD_MS = 3000
const SECONDS = HOLD_MS / 1000

/** Under så här mycket räknas ett släpp som en råkning, inte som ett avbrott. */
const SLIP_THRESHOLD = 0.12

/**
 * Så länge ett håll överlever att kontakten bryts.
 *
 * setPointerCapture löser att fingret *glider*, men inte att det tappar
 * kontakt – och en darrig tumme, en kall hand eller en skärm med
 * fingeravtryck bryter kontakten i femtio millisekunder utan att någon
 * släppt med avsikt. Utan respit började man om från noll varje gång, med
 * en kassör som väntade. Man slutar försöka.
 *
 * Tiden som redan hållits räknas ihop över avbrottet, så ett håll på 2,9
 * sekunder plus ett blipp inte blir 0,1 sekunder utan 2,9.
 */
const GRACE_MS = 400

type Phase = 'idle' | 'holding' | 'done'

export function HoldToRedeem({
  onComplete,
  disabled,
  busy,
  label = 'Håll in för att använda',
  description,
}: {
  onComplete: () => void
  disabled?: boolean
  busy?: boolean
  label?: string
  /** Vad som löses in, för bekräftelserutan: "25 % hos Ica Maxi". */
  description?: string
}) {
  const [phase, setPhase] = useState<Phase>('idle')
  const [secondsLeft, setSecondsLeft] = useState(SECONDS)
  const [releasedEarly, setReleasedEarly] = useState(false)

  const [confirming, setConfirming] = useState(false)

  const timers = useRef<number[]>([])
  const startedAt = useRef(0)
  /** Hålltid från tidigare delsträckor, innan kontakten bröts. */
  const heldMs = useRef(0)
  const graceTimer = useRef(0)

  const clearTimers = useCallback(() => {
    for (const timer of timers.current) window.clearTimeout(timer)
    timers.current = []
  }, [])

  const cancel = useCallback(() => {
    clearTimers()
    window.clearTimeout(graceTimer.current)
    graceTimer.current = 0

    const total = heldMs.current + (startedAt.current ? performance.now() - startedAt.current : 0)
    const progress = total / HOLD_MS
    // Hann man knappt trycka var det ett feltryck, inte ett avbrutet håll –
    // då är en förklarande text bara i vägen.
    if (progress > SLIP_THRESHOLD && progress < 1) setReleasedEarly(true)

    startedAt.current = 0
    heldMs.current = 0
    setPhase('idle')
    setSecondsLeft(SECONDS)
  }, [clearTimers])

  /**
   * Kontakten bröts – men hållet är inte avbrutet förrän respiten gått ut.
   *
   * Tiden bokförs, timrarna stoppas, och kommer fingret tillbaka inom
   * GRACE_MS fortsätter nedräkningen där den var. Fyllnaden sköter sig själv:
   * den är en CSS-transition, och en transition retargetar från det värde den
   * faktiskt står på i stället för att hoppa till noll.
   */
  const release = useCallback(() => {
    if (phase !== 'holding') return

    clearTimers()
    if (startedAt.current) {
      heldMs.current += performance.now() - startedAt.current
      startedAt.current = 0
    }

    window.clearTimeout(graceTimer.current)
    graceTimer.current = window.setTimeout(cancel, GRACE_MS)
  }, [cancel, clearTimers, phase])

  const begin = useCallback(() => {
    if (disabled || busy || phase === 'done') return

    // Återupptaget håll: respiten avbryts och vi fortsätter räkna.
    window.clearTimeout(graceTimer.current)
    graceTimer.current = 0

    const remaining = Math.max(0, HOLD_MS - heldMs.current)
    if (remaining === 0) return

    setReleasedEarly(false)
    startedAt.current = performance.now()
    setPhase('holding')
    setSecondsLeft(Math.ceil(remaining / 1000))

    // "Jag hörde dig" – den viktigaste återkopplingen av alla, och den som
    // saknades helt tidigare. Första vibrationen kom först efter en sekund.
    navigator.vibrate?.(10)

    for (let tick = 1; tick * 1000 < remaining; tick++) {
      timers.current.push(
        window.setTimeout(() => {
          setSecondsLeft(Math.ceil((remaining - tick * 1000) / 1000))
          navigator.vibrate?.(8)
        }, tick * 1000),
      )
    }

    timers.current.push(
      window.setTimeout(() => {
        clearTimers()
        startedAt.current = 0
        heldMs.current = 0
        setSecondsLeft(0)
        setPhase('done')
        // Kort "låst", inte en fanfar. Fanfaren hör hemma vid stämpelns
        // nedslag, som kommer efter nätverksanropet.
        navigator.vibrate?.(18)
        onComplete()
      }, remaining),
    )
  }, [busy, clearTimers, disabled, onComplete, phase])

  useEffect(
    () => () => {
      clearTimers()
      window.clearTimeout(graceTimer.current)
    },
    [clearTimers],
  )

  const holding = phase === 'holding'
  const content = busy
    ? 'Registrerar…'
    : holding
      ? `Håll kvar… ${secondsLeft}`
      : phase === 'done'
        ? 'Klart'
        : label

  return (
    <div className="space-y-2" data-hold={phase} style={{ '--hold-ms': `${HOLD_MS}ms` } as never}>
      <button
        type="button"
        disabled={disabled || busy}
        // setPointerCapture gör att gesten följer fingret även om det glider
        // utanför knappens kant. Utan den nollställdes hela hållet tyst så
        // fort tummen rörde sig några pixlar – med en hand i en kassakö
        // hände det ofta.
        onPointerDown={(event) => {
          try {
            event.currentTarget.setPointerCapture(event.pointerId)
          } catch {
            // Capture är en förbättring, inte ett krav. Skulle den vägra får
            // hållet ändå starta – annars vore knappen helt död.
          }
          begin()
        }}
        onPointerUp={release}
        onPointerCancel={release}
        onKeyDown={(event) => {
          if ((event.key === ' ' || event.key === 'Enter') && !event.repeat) {
            event.preventDefault()
            begin()
          }
        }}
        onKeyUp={(event) => {
          if (event.key === ' ' || event.key === 'Enter') release()
        }}
        /*
         * Hjälpmedel aktiverar knappar med en syntetisk klickhändelse.
         *
         * VoiceOvers dubbeltryck, röststyrning, kontaktstyrning och NVDA i
         * bläddringsläge skickar alla `click` – aldrig ett par pekhändelser
         * tre sekunder isär. Utan den här grenen hände ingenting alls när de
         * tryckte: knappen var död, utan felmeddelande. En person med
         * skärmläsare kunde inte lösa in en enda rabatt.
         *
         * detail === 0 skiljer den syntetiska klicken från en riktig, som
         * alltid bär ett klickantal. Den vanliga gesten är alltså oförändrad
         * för alla andra – och tangentbordets Space och Enter ger ingen click
         * alls, eftersom onKeyDown redan kallar preventDefault.
         */
        onClick={(event) => {
          if (event.detail === 0) setConfirming(true)
        }}
        onContextMenu={(event) => event.preventDefault()}
        aria-describedby="hold-help"
        className={cn(
          'relative h-16 w-full touch-none overflow-hidden rounded-pill select-none',
          'bg-brand font-display text-lg font-extrabold text-accent',
          'transition-[scale] duration-[120ms] ease-out disabled:opacity-50',
          holding && 'scale-[0.97]',
        )}
      >
        <span aria-hidden className="hold-fill absolute inset-0 bg-accent" />

        {/* Etiketten två gånger: gul underst, mörk överst och klippt till
            samma bredd som fyllnaden. Ordningen spelar roll – låg den mörka
            underst skulle den gula måla över och texten bli gul mot gult. */}
        <span className="relative flex items-center justify-center gap-2">
          {busy && <Loader2 size={20} className="animate-spin" aria-hidden />}
          {content}
        </span>
        <span
          aria-hidden
          className="hold-label absolute inset-0 flex items-center justify-center gap-2 text-on-accent"
        >
          {content}
        </span>
      </button>

      <p id="hold-help" className="text-center text-xs text-muted">
        {releasedEarly
          ? 'Du släppte för tidigt – håll kvar tills knappen fyllts helt.'
          : 'Håll knappen intryckt i 3 sekunder. Rabatten används upp direkt och går inte att få tillbaka – håll bara in när du står vid kassan.'}
      </p>

      {/*
       * Den synliga vägen förbi gesten.
       *
       * Dämpad och underordnad, eftersom hållet är det avsedda sättet – men
       * synlig, för den som inte kan hålla in ska slippa gissa att det finns
       * ett alternativ. Den hjälper också alla som inte använder hjälpmedel:
       * tremor, en gipsad hand, en telefon i en hållare.
       */}
      {!busy && phase !== 'done' && (
        <button
          type="button"
          disabled={disabled}
          onClick={() => setConfirming(true)}
          className={cn(
            'tap-safe mx-auto block text-xs font-semibold text-brand-ink underline',
            'disabled:pointer-events-none disabled:opacity-50',
          )}
        >
          Går det inte att hålla in?
        </button>
      )}

      {/* Skärmläsare får en löpande statusuppdatering utan att texten flimrar. */}
      <span className="sr-only" role="status">
        {holding ? `Håller in, ${secondsLeft} sekunder kvar` : ''}
      </span>

      {confirming && (
        <Modal
          open
          onClose={() => setConfirming(false)}
          title="Använd rabatten?"
          size="sm"
          footer={
            <div className="flex gap-2">
              <Button variant="quiet" fullWidth onClick={() => setConfirming(false)}>
                Avbryt
              </Button>
              <Button
                fullWidth
                onClick={() => {
                  setConfirming(false)
                  onComplete()
                }}
              >
                Ja, använd
              </Button>
            </div>
          }
        >
          {/*
           * Två avsiktliga tryck i stället för tre sekunders håll.
           *
           * Skyddet mot ett oavsiktligt tryck är lika gott – ett fickklick
           * öppnar inte en dialog och trycker sedan på rätt knapp i den – och
           * kassören hinner titta på skärmen lika länge.
           */}
          <div className="space-y-3">
            {description && <p className="font-display text-lg font-extrabold">{description}</p>}
            <p className="text-sm text-ink">
              Rabatten förbrukas nu och går inte att få tillbaka. Tryck bara ja när du står vid
              kassan och personalen ser skärmen.
            </p>
          </div>
        </Modal>
      )}
    </div>
  )
}
